
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth";
import { fetchReagentById } from "@/services/reagents/fetchReagentById";
import { updateReagent } from "@/services/reagentRequests/operations/updateReagentService";
import { Reagent } from "@/types/reagent.types";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { REAGENT_IMAGES_BUCKET_NAME, uploadFile } from "@/services/storageService";
import { dateToYYYYMMDD } from "@/utils/dateUtils";

export const useEditReagent = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: "",
    catalogNumber: "",
    category: "",
    description: "",
    amount: "",
    unit: "g",
    storageConditions: "",
    numberOfPackages: "",
  });
  
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Fetch reagent data
  const { 
    data: reagent, 
    isLoading, 
    error,
    refetch: refetchReagents
  } = useQuery({
    queryKey: ['reagent', id],
    queryFn: () => fetchReagentById(id as string),
    enabled: !!id,
  });
  
  // Populate form with reagent data when available
  useEffect(() => {
    if (reagent) {
      // Extract amount and unit
      let amount = "";
      let unit = "g";
      
      if (reagent.amount) {
        // Convert amount to string if it's not already
        amount = typeof reagent.amount === 'string' ? reagent.amount : `${reagent.amount}`;
        unit = reagent.amount_unit || "g";
      }
      
      setFormData({
        name: reagent.name || "",
        catalogNumber: reagent.catalogue_number || reagent.catalogNumber || "",
        category: reagent.category_id || reagent.category || "",
        description: reagent.description || "",
        amount,
        unit,
        storageConditions: reagent.storage_conditions || reagent.storageConditions || "",
        numberOfPackages: reagent.number_of_packages ? 
          (typeof reagent.number_of_packages === 'string' ? 
            reagent.number_of_packages : 
            String(reagent.number_of_packages)) : 
          reagent.numberOfPackages || "",
      });
    }
  }, [reagent]);
  
  // Check if user is owner of this reagent
  useEffect(() => {
    if (reagent && user && reagent.user_id !== user.id) {
      toast.error("You don't have permission to edit this reagent");
      navigate("/my-reagents");
    }
  }, [reagent, user, navigate]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleImageChange = (file: File | null) => {
    setSelectedImage(file);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id || !user) return;
    
    setIsSubmitting(true);
    
    try {
      let imageUrl = undefined;
      
      // Upload new image if selected
      if (selectedImage) {
        try {
          const fileName = `${uuidv4()}-${selectedImage.name.replace(/\s+/g, '-')}`;
          const filePath = `${user.id}/${fileName}`;
          
          console.log("Uploading image:", filePath);
          
          imageUrl = await uploadFile(
            selectedImage,
            REAGENT_IMAGES_BUCKET_NAME,
            filePath
          );
          
          console.log("Image uploaded successfully:", imageUrl);
        } catch (uploadError) {
          console.error("Error uploading image:", uploadError);
          toast.error("Failed to upload image. Please try again.");
          setIsSubmitting(false);
          return;
        }
      }
      
      // Prepare data for update
      const updateData: any = {
        name: formData.name,
        catalogue_number: formData.catalogNumber,
        category_id: formData.category || null,
        description: formData.description,
        amount: formData.amount,
        amount_unit: formData.unit,
        storage_conditions: formData.storageConditions,
        number_of_packages: formData.numberOfPackages || null,
      };
      
      // Only add image_url if a new image was uploaded
      if (imageUrl) {
        updateData.image_url = imageUrl;
      }
      
      console.log("Sending update with data:", updateData);
      // Use updateReagent instead of updateReagentRequest
      const success = await updateReagent(id, updateData);
      
      if (success) {
        toast.success("Reagent updated successfully");
        // Navigate back to the My Reagents page
        navigate("/my-reagents");
      } else {
        toast.error("Failed to update reagent");
      }
    } catch (err) {
      console.error("Error updating reagent:", err);
      toast.error("An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleCancel = () => {
    navigate("/my-reagents");
  };

  return {
    reagent,
    isLoading,
    error,
    formData,
    selectedImage,
    isSubmitting,
    handleChange,
    handleSelectChange,
    handleImageChange,
    handleSubmit,
    handleCancel
  };
};
